import get from 'lodash.get';
import React from 'react';
import getHighlightText from '../../../utils/getHighlightText.js';
import { Option } from '../Option.js';
import { jsx } from 'react/jsx-runtime';
import { isArray, isObject, isString, isNumber } from '../../../utils/is.js';

const preventDefaultEvent = e => {
  e && e.preventDefault();
};
const isEmptyValue = (value, isMultiple) => {
  // Illegal value is considered as unselected
  return isMultiple ? !isArray(value) || !value.length : value === undefined;
};
const isSelectOption = child => {
  return get(child, 'props.isSelectOption') || get(child, 'props.originalType.defaultProps.isSelectOption');
};
const isSelectOptGroup = child => {
  return get(child, 'props.isSelectOptGroup') || get(child, 'props.originalType.defaultProps.isSelectOptGroup');
};
const flatChildren = ({
  children,
  options,
  filterOption
}, {
  inputValue = '',
  userCreatedOptions,
  userCreatingOption
}, {
  optionInfoMap = new Map(),
  optionValueList = [],
  optionLabelList = [],
  customNodeCount = 0
} = {}) => {
  let hasOptGroup = false;
  let childrenList = [];
  let optionIndexListForArrowKey = [];
  const getChildValue = child => {
    return get(child, 'props.value');
  };
  const getChildLabel = child => {
    return get(child, 'props.label');
  };
  const getChildKey = ({
    label,
    value
  }, key, isGroupTitle) => {
    if (!label && !value && !key) {
      customNodeCount++;
      return `custom_node_${customNodeCount}`;
    }
    return isGroupTitle ? key || `group_${label}` : key || `${typeof value}_${value}` || `${label}_${optionInfoMap.size}`;
  };
  const handleOption = (child, origin) => {
    const optionValue = getChildValue(child);
    const optionLabel = getChildLabel(child);
    let isValidOption = true;
    if (filterOption === true) {
      isValidOption = optionLabel !== undefined && String(optionLabel).toLowerCase().indexOf(inputValue.toLowerCase()) !== -1;
    } else if (typeof filterOption === 'function') {
      isValidOption = !inputValue || filterOption(inputValue, child);
    }
    if (!optionInfoMap.get(optionValue)) {
      if (!('_key' in child.props)) {
        child = /*#__PURE__*/React.cloneElement(child, {
          _key: getChildKey(child.props, child.key)
        });
      }
      const index = optionInfoMap.size;
      const option = {
        child,
        ...child.props,
        value: optionValue,
        label: optionLabel,
        _index: index,
        _origin: origin,
        _valid: isValidOption
      };
      optionInfoMap.set(optionValue, option);
      optionValueList.push(optionValue);
      optionLabelList.push(optionLabel);
      if (isValidOption) {
        childrenList.push(child);
        if (!option.disabled) {
          optionIndexListForArrowKey.push(index);
        }
      }
    }
  };
  const extendChildren = (arr, origin) => {
    if (origin && isArray(arr) && arr.length) {
      // eslint-disable-next-line prettier/prettier
      const _arr = arr;
      _arr.forEach(option => {
        option = isString(option) || isNumber(option) ? {
          label: option,
          value: option
        } : option;
        const child = /*#__PURE__*/jsx(Option, {
          _key: getChildKey(option),
          value: option.value,
          disabled: option.disabled === true,
          label: option.label,
          children: option.content || option.label
        });
        handleOption(child, origin);
      });
    }
  };
  if (userCreatingOption) {
    extendChildren([userCreatingOption], 'userCreatingOption');
  }
  if (children) {
    React.Children.map(children, child => {
      if (isSelectOptGroup(child)) {
        const {
          children,
          options
        } = child.props;
        const {
          childrenList: _childrenList,
          optionIndexListForArrowKey: _optionIndexListForArrowKey
        } = flatChildren({
          children,
          options,
          filterOption
        }, {
          inputValue
        }, {
          optionInfoMap,
          optionValueList,
          optionLabelList,
          customNodeCount
        });
        if (_childrenList.length) {
          childrenList.push( /*#__PURE__*/React.cloneElement(child, {
            children: null,
            _key: getChildKey(child.props, child.key, true)
          }));
          childrenList = childrenList.concat(_childrenList);
          optionIndexListForArrowKey = optionIndexListForArrowKey.concat(_optionIndexListForArrowKey);
          hasOptGroup = true;
        }
      } else if (isSelectOption(child)) {
        handleOption(child, 'children');
      } else if (isObject(child) && child.props) {
        childrenList.push( /*#__PURE__*/React.cloneElement(child, {
          _key: getChildKey(child.props, child.key)
        }));
      }
    });
  }
  extendChildren(options, 'options');
  extendChildren(userCreatedOptions, 'userCreatedOptions');
  return {
    childrenList: getHighlightText({
      nodeList: childrenList,
      pattern: inputValue,
      highlightClassName: `select_highlight`
    }),
    optionInfoMap,
    optionValueList,
    optionLabelList,
    optionIndexListForArrowKey,
    hasOptGroup
  };
};
const getValidValue = (value, isMultiple, options, children) => {
  const flatChildrenList = flatChildren({
    children,
    options
  }, {
    inputValue: ''
  });
  const {
    optionInfoMap
  } = flatChildrenList;
  const _option = [];
  if (Array.isArray(value)) {
    value.forEach(val => {
      const opt = optionInfoMap.get(val);
      if (opt && opt._valid) {
        _option.push(opt?.label);
      }
      return _option;
    }, []);
  } else {
    const opt = optionInfoMap.get(value);
    if (opt && opt._valid) {
      _option.push(opt.label);
    } else {
      _option.push('');
    }
  }
  const _v = Array.isArray(_option) ? _option : [];
  const _isMultiple = isMultiple ? _v : [];
  return isMultiple ? _isMultiple : _option?.[0];
};

export { flatChildren, getValidValue, isEmptyValue, isSelectOptGroup, isSelectOption, preventDefaultEvent };
